import React, { useCallback, useMemo } from 'react';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ModalDialog from '../../../../ModalDialog';
import { PageTitle } from '../../../../Typograpgy/Title';
import { useTeaserContext } from '../TeaserContext';
import { ModalProps } from '@mui/material/Modal';
import { GetAvailabilityRequest, reverseTripStore } from '#/store/ReverseTripStore';
import { formatCurrency } from '#/components/CurrencyFormatter/CurrencyFormatter';
import siteStore from '#/store/SitesStore';
import { observer } from 'mobx-react-lite';
import { JucyButton } from '#/components/Button/Button';
import CachedIcon from '@mui/icons-material/Cached';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import analytics from '#/services/analytics';
import availabilityStore from '#/store/AvailabilityStore';
import { FleetCategoryFromJSON } from '@jucy/rentals-api-client/rentals-api-v3/models/FleetCategory';
import { useActiveRoute, useSearchParams } from '#/hooks';
import { toJS } from 'mobx';
import { useParams } from 'react-router';
import RouteRegistry from '#/store/RouteRegistry';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TranslationStore from '#/store/TranslationStore';
import ReactMarkdown from 'react-markdown';

const ReverseTripDetailsDialog = observer(({ onClose, open }: { onClose?: ModalProps['onClose']; open: ModalProps['open'] }) => {
    const { fleetCategory } = useTeaserContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeRoute = useActiveRoute();
    const activeParams = useParams();
    const newTrip = toJS(reverseTripStore.reverseTrip) as GetAvailabilityRequest;
    const { t } = useTranslation(TranslationStore.currentNamespace);

    const continueLink = useMemo(
        () =>
            RouteRegistry.getPath(activeRoute?.id as string, activeParams, {
                ...searchParams,
                pickUpLocation: newTrip.pickUpLocation,
                dropOffLocation: newTrip.dropOffLocation,
            }),
        [activeRoute?.id, activeParams, searchParams, newTrip.pickUpLocation, newTrip.dropOffLocation]
    );

    const handleCancelClicked = useCallback(() => {
        onClose?.({}, 'backdropClick');
    }, [onClose]);

    const handleSwapTripClicked = useCallback(() => {
        analytics.plugins.interactions.trackInteraction({
            source: 'OBE',
            category: 'reverse-trip-swap',
            action: 'click',
            label: `${fleetCategory.product.code} ${reverseTripStore.trip.pickUpLocation} to ${reverseTripStore.trip.dropOffLocation}`,
        });
        availabilityStore.updateResults({
            ...newTrip,
            rentalDays: fleetCategory.rentalDays,
            fleetCategories: reverseTripStore.reverseResults.map((f) => FleetCategoryFromJSON(f)),
        });
        setSearchParams({
            ...searchParams,
            pickUpLocation: newTrip.pickUpLocation,
            dropOffLocation: newTrip.dropOffLocation,
        });
        reverseTripStore.setTrip(newTrip);
        onClose?.({}, 'backdropClick');
    }, [fleetCategory.product.code, fleetCategory.rentalDays, newTrip, onClose, searchParams, setSearchParams]);

    return (
        <ModalDialog open={open} onClose={onClose} maxWidth="sm" keepMounted={false}>
            <DialogTitle component="div">
                <PageTitle>
                    <CachedIcon /> {t('fleet_result_list.reverse_trip.details_title')}
                </PageTitle>
            </DialogTitle>
            <ReverseTripDetailsDialogContainer />
            <DialogActions>
                <JucyButton onClick={handleCancelClicked} startIcon={<CloseIcon />} variant="outlined">
                    Cancel
                </JucyButton>
                <JucyButton component={Link} to={continueLink} onClick={handleSwapTripClicked} startIcon={<CachedIcon />} variant="contained">
                    Swap now
                </JucyButton>
            </DialogActions>
        </ModalDialog>
    );
});

const ReverseTripDetailsDialogContainer = observer(() => {
    const { fleetCategory } = useTeaserContext();
    const comparison = reverseTripStore?.reverseComparison?.[fleetCategory.product.code];
    const pickUpLocation = reverseTripStore?.trip.pickUpLocation ? siteStore.getSiteByCode(reverseTripStore.trip.pickUpLocation) : undefined;
    const dropOffLocation = reverseTripStore?.trip.dropOffLocation ? siteStore.getSiteByCode(reverseTripStore.trip.dropOffLocation) : undefined;
    const pickUpDate = reverseTripStore?.trip.pickUpDate;
    const dropOffDate = reverseTripStore?.trip.dropOffDate;
    const { t } = useTranslation(TranslationStore.currentNamespace);

    if (!comparison || !pickUpLocation || !dropOffLocation || !pickUpDate || !dropOffDate) {
        return null;
    }

    return (
        <DialogContent>
            <ReactMarkdown>
                {t('fleet_result_list.reverse_trip.details', {
                    dropOffLocation: dropOffLocation.Name,
                    pickUpLocation: pickUpLocation.Name,
                })}
            </ReactMarkdown>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Direction</TableCell>
                            <TableCell align="right">Vehicle</TableCell>
                            <TableCell align="right">Hire price</TableCell>
                            <TableCell align="right">You save</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell component="th" scope="row">
                                {pickUpLocation.Name} <br />
                                to
                                <br /> {dropOffLocation.Name}
                            </TableCell>
                            <TableCell align="right">{fleetCategory.product.name}</TableCell>
                            <TableCell align="right">{formatCurrency(comparison.forward)}</TableCell>
                            <TableCell align="right">-</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" scope="row">
                                {dropOffLocation.Name} <br />
                                to
                                <br /> {pickUpLocation.Name}
                            </TableCell>
                            <TableCell align="right">{fleetCategory.product.name}</TableCell>
                            <TableCell align="right">{formatCurrency(comparison.reverse)}</TableCell>
                            <TableCell align="right">{formatCurrency(-comparison.diff)}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
    );
});
export default ReverseTripDetailsDialog;
